import { useCallback, useEffect, useMemo, useState } from 'react';
import { addDays, type Contact, type Person, type SchemaOptions, type User } from '@crmsy/shared';
import { todayIso } from './today.js';
import { dispatch } from './actions.js';
import { useKeys, type KeyMap } from './keyboard.js';
import { toast } from './toast.js';
import { Picker, type PickerOption } from '../components/Picker.jsx';
import { DatePick } from '../components/DatePick.jsx';
import { ArchiveConfirm, EditDialog, FollowedUpDialog, NoteDialog } from '../components/Dialogs.jsx';
import { MessageDialog } from '../components/MessageDialog.jsx';
import { useAiEnabled } from './features.js';
import { t, type Key } from '../i18n/index.js';

export type RecordDialog = 'stage' | 'nextAction' | 'status' | 'owner' | 'date' | 'followedUp' | 'snooze' | 'note' | 'edit' | 'archive' | 'message';

const FIELD_PROPERTY = { stage: 'Stage', nextAction: 'Next Action', status: 'Status' } as const;
const FIELD_TITLE: Record<'stage' | 'nextAction' | 'status', Key> = { stage: 'set.stage', nextAction: 'set.nextAction', status: 'set.status' };
const RECORD_HOTKEYS: Record<string, RecordDialog> = { s: 'stage', a: 'nextAction', t: 'status', o: 'owner' };

export interface RecordActionsOptions {
  /** `record`: the panel, every letter sets a field (§7). `list`: a selected row, only f / z / c act on it. */
  mode: 'record' | 'list';
  priority: number;
  enabled?: boolean;
  schema: SchemaOptions;
  people: Person[];
  user: User;
}

/**
 * Keyboard + dialogs for acting on one contact (§7 record context, §6.2 card actions).
 * Returns `open(kind)` for buttons and `element` to render the active dialog.
 */
export function useRecordActions(contact: Contact | null, o: RecordActionsOptions) {
  const [dialog, setDialog] = useState<{ kind: RecordDialog; anchor: { x: number; y: number } | null } | null>(null);
  const ai = useAiEnabled();
  const enabled = (o.enabled ?? true) && !!contact;
  const today = todayIso();

  useEffect(() => {
    if (!contact) setDialog(null);
  }, [contact]);

  const open = useCallback((kind: RecordDialog, anchor: { x: number; y: number } | null = null) => {
    setDialog((d) => (d?.kind === kind ? null : { kind, anchor }));
  }, []);
  const close = useCallback(() => setDialog(null), []);

  const run = useCallback(
    (p: Promise<unknown>) =>
      p.catch((e: unknown) => toast(e instanceof Error ? e.message : String(e), { kind: 'error' })),
    [],
  );

  const setField = useCallback(
    (field: 'stage' | 'nextAction' | 'status' | 'owner' | 'nextContactDate', value: string | string[] | null) => {
      if (!contact) return;
      void run(dispatch('setField', { contactId: contact.id, field, value }));
    },
    [contact, run],
  );

  const assignToMe = useCallback(() => {
    if (!contact) return;
    if (!o.user.notionUserId) {
      toast(t('toast.unmapped'), { kind: 'error' });
      return;
    }
    setField('owner', [o.user.notionUserId]);
  }, [contact, o.user.notionUserId, setField]);

  const snoozeTo = useCallback(
    (date: string) => {
      if (!contact) return;
      void run(dispatch('snooze', { contactId: contact.id, nextContactDate: date }));
    },
    [contact, run],
  );

  const recordKeys = useMemo<KeyMap>(
    () =>
      o.mode === 'record'
        ? {
            s: () => open('stage'),
            a: () => open('nextAction'),
            t: () => open('status'),
            o: () => open('owner'),
            i: assignToMe,
            d: () => open('date'),
            w: () => snoozeTo(addDays(today, 7)),
            e: () => open('edit'),
            m: () => open('message'),
          }
        : ({} as KeyMap),
    [o.mode, open, assignToMe, snoozeTo, today],
  );

  useKeys(
    {
      ...recordKeys,
      f: () => open('followedUp'),
      z: () => open('snooze'),
      c: () => open('note'),
      Escape: () => {
        if (dialog) close();
        else return false;
      },
    },
    { priority: o.priority, enabled },
  );

  // While a picker is open, its own letter closes it (toggle), even though the picker input has focus.
  useEffect(() => {
    if (!dialog || o.mode !== 'record') return;
    const onKey = (e: KeyboardEvent) => {
      const k = RECORD_HOTKEYS[e.key];
      const input = e.target as HTMLInputElement;
      if (k && k === dialog.kind && input.tagName === 'INPUT' && input.value === '') {
        e.preventDefault();
        close();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [dialog, o.mode, close]);

  let element: React.ReactNode = null;
  if (contact && dialog) {
    const k = dialog.kind;
    if (k === 'stage' || k === 'nextAction' || k === 'status') {
      const current = contact[k];
      const options: PickerOption[] = (o.schema[FIELD_PROPERTY[k]] ?? []).map((x) => ({ value: x.name, label: x.name, color: x.color }));
      if (current && !options.some((x) => x.value === current)) options.unshift({ value: current, label: current });
      options.push({ value: '', label: t('picker.clearValue') });
      element = (
        <Picker
          title={t(FIELD_TITLE[k])}
          options={options}
          selected={current ? [current] : []}
          onToggle={(v) => setField(k, v || null)}
          onClose={close}
          anchor={dialog.anchor}
        />
      );
    } else if (k === 'owner') {
      element = (
        <Picker
          title={t('set.owner')}
          options={o.people.map((p) => ({ value: p.id, label: p.name }))}
          selected={contact.owner}
          onToggle={(v) => setField('owner', [v])}
          onClose={close}
          anchor={dialog.anchor}
        />
      );
    } else if (k === 'date') {
      element = <DatePick title={t('set.nextContactDate')} current={contact.nextContactDate} allowNone onPick={(d) => setField('nextContactDate', d)} onClose={close} anchor={dialog.anchor} />;
    } else if (k === 'snooze') {
      element = <DatePick title={t('set.snoozeUntil')} current={contact.nextContactDate} onPick={(d) => d && snoozeTo(d)} onClose={close} anchor={dialog.anchor} />;
    } else if (k === 'followedUp') {
      element = (
        <FollowedUpDialog
          contact={contact}
          schema={o.schema}
          onSave={(v) => void run(dispatch('followedUp', { contactId: contact.id, nextContactDate: v.nextContactDate, nextAction: v.nextAction, note: v.note || undefined }))}
          onClose={close}
        />
      );
    } else if (k === 'message') {
      element = (
        <MessageDialog
          contact={contact}
          schema={o.schema}
          user={o.user}
          ai={ai}
          onSave={(v) => void run(dispatch('sentMessage', { contactId: contact.id, text: v.text, origin: v.origin, nextContactDate: v.nextContactDate, nextAction: v.nextAction }))}
          onClose={close}
        />
      );
    } else if (k === 'note') {
      element = <NoteDialog contact={contact} onSave={(text) => void run(dispatch('addNote', { contactId: contact.id, text }))} onClose={close} />;
    } else if (k === 'edit') {
      element = (
        <EditDialog
          contact={contact}
          onSave={(v) => {
            const pairs: ['companyName' | 'contact' | 'email' | 'phone', string | null][] = [
              ['companyName', v.companyName],
              ['contact', v.contact],
              ['email', v.email || null],
              ['phone', v.phone || null],
            ];
            for (const [field, value] of pairs) {
              if ((contact[field] ?? null) !== value && !(contact[field] === '' && value === null)) void run(dispatch('setField', { contactId: contact.id, field, value }));
            }
          }}
          onClose={close}
        />
      );
    } else if (k === 'archive') {
      element = <ArchiveConfirm contact={contact} onConfirm={() => void run(dispatch('archiveProspect', { contactId: contact.id }))} onClose={close} />;
    }
  }

  return { open, close, element, dialog: dialog?.kind ?? null, assignToMe, snoozeTo };
}
